//  _        _      _         _
// | |  _  _| |_  _| |__ _  _| |__ _  _
// | |_| || | | || | '_ \ || | '_ \ || |
// |____\_,_|_|\_,_|_.__/\_,_|_.__/\_,_|
//
// Copyright © Lulububu Software GmbH - All Rights Reserved
// https://lulububu.de
//
// Unauthorized copying of this file, via any medium is strictly prohibited!
// Proprietary and confidential.

import React from 'react';

import _               from 'lodash';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

import PropTypes            from '@components/PropTypes';
import ParkingType          from '@constants/ParkingType';
import UnitFields           from '@constants/UnitFields';
import InputRenderHelper    from '@helper/InputRenderHelper';
import useContextTranslator from '@hooks/ContextTranslator';
import { useUnit }          from '@slices/unit';
import DropDown             from '@stateless/atomic/DropDown';
import DropDownType         from '@stateless/atomic/DropDown/DropDownType';
import LabeledInputType     from '@stateless/atomic/LabeledInput/LabeledInputType';
import FacilityList         from '@stateless/composed/FacilityList';
import selectEditUnit       from '@store/selectors/unit';

const propTypes = {
    onUpdateData: PropTypes.func,
    validations:  PropTypes.object,
};

const ParkingSpaceList = ({
    validations,
    onUpdateData,
}) => {
    const dispatch              = useDispatch();
    const unitActions           = useUnit(dispatch);
    const unit                  = useSelector(selectEditUnit);
    const translator            = useContextTranslator('components.parkingSpaceList');
    const parkingTypesSelection = Object.keys(ParkingType).map((key) => {
        return {
            label: translator.t(key),
            value: _.get(ParkingType, key),
        };
    });
    const parkingSpaces         = _.get(unit, UnitFields.carParkingSpaces, []);
    const inputRenderer         = new InputRenderHelper({
        validations,
        data: unit,
        onUpdateData,
    });
    const gridColumnStyle       = {
        gridTemplateColumns: '2fr 3fr 3fr 1fr',
    };
    const parkingSpacesFields   = [
        {
            headerText:  translator.t('type'),
            renderInput: (index) => {
                const typeField = [UnitFields.carParkingSpaces, index, UnitFields.carParkingSpaceType];

                return (
                    <DropDown
                        dropDownType={DropDownType.white}
                        options={parkingTypesSelection}
                        value={_.get(unit, typeField)}
                        onChange={onUpdateData(typeField)}
                    />
                );
            },
        },
        {
            headerText:  translator.t('buyingPrice'),
            renderInput: (index) => inputRenderer.renderCurrencyInput({
                type:  LabeledInputType.lightWithoutLabel,
                field: [UnitFields.carParkingSpaces, index, UnitFields.carParkingSpacePurchasePrice],
            }),
        },
        {
            headerText:  translator.t('rentingPrice'),
            renderInput: (index) => inputRenderer.renderCurrencyInput({
                type:  LabeledInputType.lightWithoutLabel,
                field: [UnitFields.carParkingSpaces, index, UnitFields.carParkingSpaceMonthlyRent],
            }),
        },
    ];

    function addParkingSpaceClicked() {
        unitActions.addCarParkingSpace();
    }

    function deleteParkingSpaceClicked(index) {
        unitActions.deleteCarParkingSpace({
            index,
        });
    }

    return (
        <FacilityList
            emptyText={translator.t('noParkingSpaces')}
            addText={translator.t('addParkingSpace')}
            addFacilityClicked={addParkingSpaceClicked}
            deleteFacilityClicked={deleteParkingSpaceClicked}
            gridColumnStyle={gridColumnStyle}
            facilityList={parkingSpaces}
            facilityFields={parkingSpacesFields}
        />
    );
};

ParkingSpaceList.propTypes = propTypes;

export default ParkingSpaceList;
